import React, { useState } from 'react';
import { 
  EnvelopeIcon,  
  ChatBubbleLeftRightIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { useAppTheme } from '@/context/ThemeContext';

const ContactSupport: React.FC = () => {
  const { themeConfig } = useAppTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const subjects = [
    'General Inquiry',
    'Technical Support',
    'Billing Question',
    'Feature Request',
    'Partnership Opportunity',
    'Other'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: 'General Inquiry',
      message: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard');
  };

  return (
    <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: themeConfig.primary }}>
            Contact Support
          </h1>
          <p className="text-gray-600 mt-2">
            Get in touch with our support team. We're here to help!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Methods */}
          <div className="md:col-span-1 space-y-4">
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: themeConfig.cardBackground }}
            >
              <h2 className="text-lg font-medium mb-4" style={{ color: themeConfig.text }}>
                Contact Methods
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 mt-1 mr-3" style={{ color: themeConfig.primary }} />
                  <div>
                    <h3 className="font-medium" style={{ color: themeConfig.text }}>Email Us</h3>
                    <p className="text-sm text-gray-500">customerservice@catchoftheday.io</p>
                    <button 
                      className="text-xs flex items-center mt-1"
                      style={{ color: themeConfig.primary }}
                      onClick={() => copyToClipboard('customerservice@catchoftheday.io')}
                    >
                      <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
                
                
                <div className="flex items-start">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mt-1 mr-3" style={{ color: themeConfig.primary }} />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            <div 
              className="p-6 rounded-lg"
              style={{ backgroundColor: themeConfig.cardBackground }}
            >
              <h2 className="text-lg font-medium mb-4" style={{ color: themeConfig.text }}>
                Send us a message
              </h2>
              
              {submitSuccess ? (
                <div 
                  className="p-4 rounded-lg mb-4"
                  style={{ backgroundColor: '#d1fae5' }}
                >
                  <p className="text-green-800 font-medium">
                    Your message has been sent successfully! We'll get back to you soon.
                  </p>
                </div>
              ) : null}
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label 
                      htmlFor="name" 
                      className="block text-sm font-medium mb-1"
                      style={{ color: themeConfig.text }}
                    >
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ 
                        borderColor: themeConfig.accent,
                        backgroundColor: themeConfig.background
                      }}
                    />
                  </div>
                  
                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-sm font-medium mb-1"
                      style={{ color: themeConfig.text }}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ 
                        borderColor: themeConfig.accent,
                        backgroundColor: themeConfig.background
                      }}
                    />
                  </div>
                  
                  <div>
                    <label 
                      htmlFor="subject" 
                      className="block text-sm font-medium mb-1"
                      style={{ color: themeConfig.text }}
                    >
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ 
                        borderColor: themeConfig.accent,
                        backgroundColor: themeConfig.background
                      }}
                    >
                      {subjects.map(subject => (
                        <option key={subject} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-medium mb-1"
                      style={{ color: themeConfig.text }}
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ 
                        borderColor: themeConfig.accent,
                        backgroundColor: themeConfig.background
                      }}
                    />
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-2 px-4 rounded-md font-medium"
                      style={{ 
                        backgroundColor: themeConfig.primary,
                        color: 'white',
                        opacity: isSubmitting ? 0.7 : 1
                      }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
    </div>
  );
}

export default ContactSupport;
